import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { REVIEW_QUEUE } from '../lib/mock-data';
import { CheckSquare, AlertTriangle, Check, X, Eye, Filter, Clock, FileText, User, ChevronRight, AlertCircle, FileSearch, Tag, Shield, PenTool, GitMerge } from 'lucide-react';
export function ReviewQueue() {
  const [selectedFilter, setSelectedFilter] = useState<string>('all');
  const [selectedItem, setSelectedItem] = useState<string | null>(REVIEW_QUEUE[0]?.id || null);
  const selectedReview = REVIEW_QUEUE.find(r => r.id === selectedItem);
  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'Chronology Conflict':
        return <GitMerge className="w-4 h-4" data-id="element-1985" />;
      case 'Classification':
        return <Tag className="w-4 h-4" data-id="element-1986" />;
      case 'Draft Review':
        return <PenTool className="w-4 h-4" data-id="element-1987" />;
      case 'Privilege Flag':
        return <Shield className="w-4 h-4" data-id="element-1988" />;
      default:
        return <FileText className="w-4 h-4" data-id="element-1989" />;
    }
  };
  const getTypeColor = (type: string) => {
    switch (type) {
      case 'Chronology Conflict':
        return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'Classification':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'Draft Review':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'Privilege Flag':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };
  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'High':
        return 'bg-red-500';
      case 'Medium':
        return 'bg-amber-500';
      case 'Low':
        return 'bg-emerald-500';
      default:
        return 'bg-muted';
    }
  };
  const filteredQueue = selectedFilter === 'all' ? REVIEW_QUEUE : REVIEW_QUEUE.filter(r => r.type === selectedFilter);
  const typeCounts = REVIEW_QUEUE.reduce((acc, item) => {
    acc[item.type] = (acc[item.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  return <div className="space-y-6 h-[calc(100vh-8rem)] flex flex-col" data-id="element-1990">
      {/* Header */}
      <div className="shrink-0" data-id="element-1991">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-1992">
          <div data-id="element-1993">
            <h1 className="text-2xl font-bold tracking-tight flex items-center" data-id="element-1994">
              <CheckSquare className="w-6 h-6 mr-3 text-amber-600" data-id="element-1995" />
              Review Queue
            </h1>
            <p className="text-muted-foreground mt-1" data-id="element-1996">
              Human-in-the-loop approval checkpoints for AI-generated outputs.
              All items require attorney review.
            </p>
          </div>
          <div className="flex items-center space-x-3" data-id="element-1997">
            <Badge variant="outline" className="px-3 py-1.5 text-sm bg-amber-50 text-amber-800 border-amber-200" data-id="element-1998">

              <AlertCircle className="w-4 h-4 mr-1.5" data-id="element-1999" />
              {REVIEW_QUEUE.length} Pending
            </Badge>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="shrink-0 flex flex-wrap gap-2 border-b pb-4" data-id="element-2000">
        <button onClick={() => setSelectedFilter('all')} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${selectedFilter === 'all' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`} data-id="element-2001">

          All ({REVIEW_QUEUE.length})
        </button>
        {Object.entries(typeCounts).map(([type, count]) => <button key={type} onClick={() => setSelectedFilter(type)} className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center space-x-1.5 ${selectedFilter === type ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`} data-id="element-2002">

            {getTypeIcon(type)}
            <span data-id="element-2003">{type}</span>
            <span className="ml-1" data-id="element-2004">({count})</span>
          </button>)}
      </div>

      {/* Two-Panel Layout */}
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 gap-6 min-h-0 overflow-hidden" data-id="element-2005">
        {/* Left Panel: Queue List */}
        <div className="lg:col-span-5 flex flex-col overflow-hidden" data-id="element-2006">
          <Card className="flex-1 flex flex-col overflow-hidden shadow-sm" data-id="element-2007">
            <CardHeader className="py-3 border-b shrink-0 bg-muted/30" data-id="element-2008">
              <div className="flex items-center justify-between" data-id="element-2009">
                <CardTitle className="text-sm font-medium" data-id="element-2010">
                  Pending Items
                </CardTitle>
                <Button variant="ghost" size="sm" className="h-7 text-xs" data-id="element-2011">
                  <Filter className="w-3 h-3 mr-1" data-id="element-2012" /> Sort
                </Button>
              </div>
            </CardHeader>
            <div className="flex-1 overflow-y-auto" data-id="element-2013">
              <div className="divide-y" data-id="element-2014">
                {filteredQueue.map(item => {
                const isSelected = item.id === selectedItem;
                return <div key={item.id} onClick={() => setSelectedItem(item.id)} className={`p-4 cursor-pointer transition-colors border-l-4 ${isSelected ? 'bg-blue-50 border-l-blue-500' : 'hover:bg-muted/30 border-l-transparent'}`} data-id="element-2015">

                      <div className="flex items-start justify-between mb-2" data-id="element-2016">
                        <div className="flex items-center space-x-2" data-id="element-2017">
                          <div className={`w-2 h-2 rounded-full ${getPriorityColor(item.priority)}`} title={`${item.priority} Priority`} data-id="element-2018" />

                          <Badge variant="outline" className={`text-[10px] ${getTypeColor(item.type)}`} data-id="element-2019">

                            {item.type}
                          </Badge>
                        </div>
                        <span className="text-xs text-muted-foreground" data-id="element-2020">
                          {item.date}
                        </span>
                      </div>
                      <h4 className="text-sm font-medium text-foreground mb-1 line-clamp-1" data-id="element-2021">
                        {item.task}
                      </h4>
                      <p className="text-xs text-muted-foreground truncate mb-2" data-id="element-2022">
                        {item.matter}
                      </p>
                      <div className="flex items-center justify-between" data-id="element-2023">
                        <div className="flex items-center space-x-1 text-xs text-muted-foreground" data-id="element-2024">
                          <User className="w-3 h-3" data-id="element-2025" />
                          <span data-id="element-2026">{item.assignedTo}</span>
                        </div>
                        <Badge variant={item.aiConfidence > 85 ? 'success' : item.aiConfidence > 70 ? 'warning' : 'destructive'} className="text-[10px]" data-id="element-2027">

                          {item.aiConfidence}% Conf.
                        </Badge>
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </Card>
        </div>

        {/* Right Panel: Detail View */}
        <div className="lg:col-span-7 flex flex-col overflow-hidden" data-id="element-2028">
          {selectedReview ? <Card className="flex-1 flex flex-col overflow-hidden shadow-md border-l-4 border-l-amber-500" data-id="element-2029">
              <CardHeader className="py-4 border-b shrink-0 bg-amber-50/30" data-id="element-2030">
                <div className="flex items-start justify-between" data-id="element-2031">
                  <div data-id="element-2032">
                    <div className="flex items-center space-x-2 mb-2" data-id="element-2033">
                      <Badge variant="outline" className={`${getTypeColor(selectedReview.type)}`} data-id="element-2034">

                        {getTypeIcon(selectedReview.type)}
                        <span className="ml-1" data-id="element-2035">{selectedReview.type}</span>
                      </Badge>
                      <Badge variant={selectedReview.priority === 'High' ? 'destructive' : selectedReview.priority === 'Medium' ? 'warning' : 'secondary'} className="text-[10px]" data-id="element-2036">

                        {selectedReview.priority} Priority
                      </Badge>
                    </div>
                    <CardTitle className="text-lg" data-id="element-2037">
                      {selectedReview.task}
                    </CardTitle>
                    <CardDescription className="mt-1" data-id="element-2038">
                      {selectedReview.matter}
                    </CardDescription>
                  </div>
                  <div className="text-right" data-id="element-2039">
                    <div className="text-xs text-muted-foreground mb-1" data-id="element-2040">
                      AI Confidence
                    </div>
                    <div className="flex items-center space-x-2" data-id="element-2041">
                      <div className="w-20 h-2 bg-muted rounded-full overflow-hidden" data-id="element-2042">
                        <div className={`h-full rounded-full ${selectedReview.aiConfidence > 85 ? 'bg-emerald-500' : selectedReview.aiConfidence > 70 ? 'bg-amber-500' : 'bg-red-500'}`} style={{
                      width: `${selectedReview.aiConfidence}%`
                    }} data-id="element-2043" />

                      </div>
                      <span className="text-sm font-bold" data-id="element-2044">
                        {selectedReview.aiConfidence}%
                      </span>
                    </div>
                  </div>
                </div>
              </CardHeader>

              <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-2045">
                {/* Metadata Grid */}
                <div className="grid grid-cols-2 gap-4" data-id="element-2046">
                  <div className="bg-muted/30 p-3 rounded-lg border" data-id="element-2047">
                    <p className="text-xs text-muted-foreground mb-1" data-id="element-2048">
                      Assigned To
                    </p>
                    <p className="text-sm font-medium flex items-center" data-id="element-2049">
                      <User className="w-4 h-4 mr-1.5 text-muted-foreground" data-id="element-2050" />
                      {selectedReview.assignedTo}
                    </p>
                  </div>
                  <div className="bg-muted/30 p-3 rounded-lg border" data-id="element-2051">
                    <p className="text-xs text-muted-foreground mb-1" data-id="element-2052">
                      Created
                    </p>
                    <p className="text-sm font-medium flex items-center" data-id="element-2053">
                      <Clock className="w-4 h-4 mr-1.5 text-muted-foreground" data-id="element-2054" />
                      {selectedReview.date}
                    </p>
                  </div>
                </div>

                {/* Source Document */}
                <div data-id="element-2055">
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center" data-id="element-2056">
                    <FileSearch className="w-4 h-4 mr-2 text-muted-foreground" data-id="element-2057" />
                    Source Document
                  </h4>
                  <div className="bg-muted/30 p-4 rounded-lg border hover:border-blue-300 transition-colors cursor-pointer group" data-id="element-2058">
                    <div className="flex items-center justify-between" data-id="element-2059">
                      <div className="flex items-center space-x-3" data-id="element-2060">
                        <FileText className="w-5 h-5 text-blue-600" data-id="element-2061" />
                        <div data-id="element-2062">
                          <p className="text-sm font-medium text-foreground group-hover:text-blue-600 transition-colors" data-id="element-2063">
                            {selectedReview.sourceDoc}
                          </p>
                          <p className="text-xs text-muted-foreground mt-0.5" data-id="element-2064">
                            Click to open in document viewer
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-blue-600 transition-colors" data-id="element-2065" />
                    </div>
                  </div>
                </div>

                {/* Issue Description */}
                <div data-id="element-2066">
                  <h4 className="text-sm font-semibold text-foreground mb-2 flex items-center" data-id="element-2067">
                    <AlertTriangle className="w-4 h-4 mr-2 text-amber-500" data-id="element-2068" />
                    Issue Details
                  </h4>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-2069">
                    {selectedReview.type === 'Chronology Conflict' && <p className="text-sm text-amber-900" data-id="element-2070">
                        The system detected a potential conflict in the medical
                        chronology. The source document mentions information
                        that may contradict other records in the matter.
                        Attorney review is required to determine accuracy and
                        relevance.
                      </p>}
                    {selectedReview.type === 'Classification' && <p className="text-sm text-amber-900" data-id="element-2071">
                        The AI classification confidence for this document is
                        below the auto-approval threshold. Manual review is
                        required to verify the document type and ensure proper
                        categorization.
                      </p>}
                    {selectedReview.type === 'Draft Review' && <p className="text-sm text-amber-900" data-id="element-2072">
                        AI-generated draft document requires attorney review
                        before finalization. Please verify all factual
                        statements are supported by source documents and legal
                        arguments are sound.
                      </p>}
                    {selectedReview.type === 'Privilege Flag' && <p className="text-sm text-amber-900" data-id="element-2073">
                        The system detected potential privileged content in this
                        document. Review required to confirm privilege
                        designation and ensure proper handling.
                      </p>}
                  </div>
                </div>

                {/* Preview Section (Conditional based on type) */}
                {selectedReview.type === 'Chronology Conflict' && <div data-id="element-2074">
                    <h4 className="text-sm font-semibold text-foreground mb-2" data-id="element-2075">
                      Chronology Preview
                    </h4>
                    <div className="bg-white border rounded-lg overflow-hidden overflow-x-auto" data-id="element-2076">
                      <table className="w-full text-xs min-w-[400px]" data-id="element-2077">
                        <thead className="bg-muted/50 border-b" data-id="element-2078">
                          <tr data-id="element-2079">
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground" data-id="element-2080">
                              Date
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground" data-id="element-2081">
                              Event
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-muted-foreground" data-id="element-2082">
                              Source
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y" data-id="element-2083">
                          <tr className="hover:bg-muted/30" data-id="element-2084">
                            <td className="px-3 py-2 font-medium" data-id="element-2085">
                              Oct 14, 2023
                            </td>
                            <td className="px-3 py-2" data-id="element-2086">
                              Initial orthopedic consult
                            </td>
                            <td className="px-3 py-2 text-blue-600" data-id="element-2087">
                              RLSF-0056
                            </td>
                          </tr>
                          <tr className="bg-amber-50 border-l-2 border-l-amber-500" data-id="element-2088">
                            <td className="px-3 py-2 font-medium" data-id="element-2089">
                              Oct 14, 2023
                            </td>
                            <td className="px-3 py-2" data-id="element-2090">
                              <span className="bg-amber-200 text-amber-900 px-1 rounded" data-id="element-2091">
                                Patient reported prior mild back pain in 2019
                              </span>
                            </td>
                            <td className="px-3 py-2 text-blue-600" data-id="element-2092">
                              RLSF-0056
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                    <p className="text-xs text-amber-700 mt-2 flex items-center" data-id="element-2093">
                      <AlertTriangle className="w-3 h-3 mr-1" data-id="element-2094" />
                      This mention may conflict with intake questionnaire
                      stating no prior back issues.
                    </p>
                  </div>}
              </div>

              {/* Action Footer */}
              <div className="p-4 border-t bg-muted/20 shrink-0 flex items-center justify-between" data-id="element-2095">
                <Button variant="outline" size="sm" data-id="element-2096">
                  <Eye className="w-4 h-4 mr-2" data-id="element-2097" />
                  View Full Context
                </Button>
                <div className="flex space-x-2" data-id="element-2098">
                  <Button variant="outline" className="text-destructive hover:text-destructive hover:bg-destructive/10" data-id="element-2099">

                    <X className="w-4 h-4 mr-2" data-id="element-2100" />
                    Reject
                  </Button>
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" data-id="element-2101">
                    <Check className="w-4 h-4 mr-2" data-id="element-2102" />
                    Approve
                  </Button>
                </div>
              </div>
            </Card> : <Card className="flex-1 flex items-center justify-center" data-id="element-2103">
              <div className="text-center text-muted-foreground" data-id="element-2104">
                <CheckSquare className="w-12 h-12 mx-auto mb-4 text-muted-foreground/30" data-id="element-2105" />
                <p className="text-sm font-medium" data-id="element-2106">Select an item to review</p>
                <p className="text-xs mt-1" data-id="element-2107">
                  Choose from the queue on the left
                </p>
              </div>
            </Card>}
        </div>
      </div>
    </div>;
}